import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, LOGO_FULL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-azure/40";

const PasswordInput = ({ value, onChange, testid, placeholder = "••••••••", label = "Password" }) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-[0.2em] text-seastone mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          required
          value={value}
          onChange={onChange}
          data-testid={testid}
          className={`${inputCls} pr-11`}
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          data-testid={`${testid}-toggle`}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-seastone hover:text-ink transition-colors"
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
};

export default function AdminLogin() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotResult, setForgotResult] = useState(null);
  const [forgotBusy, setForgotBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotResult(data);
    } catch (err) {
      setForgotResult({ message: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-sand grid place-items-center px-5" data-testid="admin-login-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src={LOGO_FULL} alt="Mahadinusa Lembongan logo" className="w-44 mx-auto mb-4" />
          <h1 className="font-serif text-2xl" data-testid="admin-login-title">Mahadinusa Admin</h1>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-sand/50 mt-2">Lembongan · Back Office</p>
        </div>

        {!forgotOpen ? (
          <form onSubmit={submit} className="bg-sand text-ink rounded-3xl p-7 shadow-2xl space-y-4" data-testid="admin-login-form">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-[0.2em] text-seastone mb-1.5">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email-input"
                className={inputCls}
                placeholder="admin@mahadinusa.com"
              />
            </div>
            <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} testid="admin-login-password-input" />
            {error && <p className="text-sm text-coral" data-testid="admin-login-error">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit-button"
              className="w-full rounded-xl bg-ink text-sand text-sm font-medium py-3 transition-colors hover:bg-azure disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
            <div className="flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={() => { setForgotOpen(true); setForgotResult(null); }}
                data-testid="forgot-password-link"
                className="text-azure hover:underline"
              >
                Forgot password?
              </button>
              <Link to="/" data-testid="admin-login-back-link" className="text-seastone hover:text-ink">
                ← Back to website
              </Link>
            </div>
          </form>
        ) : (
          <div className="bg-sand text-ink rounded-3xl p-7 shadow-2xl space-y-4" data-testid="forgot-password-form">
            <h2 className="font-serif text-xl">Reset your password</h2>
            <p className="text-sm text-seastone leading-relaxed">
              Enter the account email. A reset link valid for 30 minutes will be generated for the admin to share with you.
            </p>
            {!forgotResult ? (
              <form onSubmit={submitForgot} className="space-y-4">
                <input
                  type="email"
                  required
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  data-testid="forgot-password-email-input"
                  className={inputCls}
                  placeholder="you@mahadinusa.com"
                />
                <button
                  type="submit"
                  disabled={forgotBusy}
                  data-testid="forgot-password-submit"
                  className="w-full rounded-xl bg-ink text-sand text-sm font-medium py-3 hover:bg-azure disabled:opacity-60"
                >
                  {forgotBusy ? "Generating…" : "Generate reset link"}
                </button>
              </form>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-ink/80" data-testid="forgot-password-message">{forgotResult.message}</p>
                {forgotResult.reset_link && (
                  <div className="rounded-xl border border-ink/15 bg-white p-3" data-testid="forgot-password-reset-link-box">
                    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-seastone mb-1.5">Reset link (valid 30 min)</p>
                    <p className="text-xs font-mono break-all text-azure" data-testid="forgot-password-reset-link">
                      {window.location.origin}{forgotResult.reset_link}
                    </p>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={() => setForgotOpen(false)}
              data-testid="forgot-password-back"
              className="text-xs text-seastone hover:text-ink"
            >
              ← Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export { PasswordInput };
